import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Trophy,
  Medal,
  Crown,
  Award,
  Zap,
  Star,
  ArrowLeft,
  GraduationCap,
  Sparkles,
  Calendar,
  Flame,
  BookOpen,
  Route,
  Loader2,
  Search,
  X,
  RefreshCw,
  ExternalLink
} from 'lucide-react';
import { userAchievementsService } from '@/services/user-achievements.service';
import { certificateRegenerationService, type RegenerationReason, type CertificateType } from '@/services/certificate-regeneration.service';
import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import Modal from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import type {
  UserAchievementDetails,
  UserBadge,
  CourseCertificate,
  CertificationCertificate,
  LearningPathCertificate,
  UserAchievementSearchType
} from '@/types/user-achievements.types';

// Badge icon mapping
const getBadgeIcon = (iconType: string) => {
  switch (iconType) {
    case 'crown': return Crown;
    case 'medal': return Medal;
    case 'trophy': return Trophy;
    case 'star': return Star;
    case 'sparkles': return Sparkles;
    case 'lightning': return Zap;
    case 'zap': return Zap;
    case 'flame': return Flame;
    case 'award': return Award;
    default: return Trophy;
  }
};

export default function UserAchievementDetail() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [activeTab, setActiveTab] = useState<'badges' | 'course' | 'certification' | 'learningpath'>('badges');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [user, setUser] = useState<UserAchievementDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const initialLoadDone = useRef(false);

  // Regenerate certificate modal state
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regenerateCertId, setRegenerateCertId] = useState<string | null>(null);
  const [regenerateCertType, setRegenerateCertType] = useState<CertificateType | null>(null);
  const [regenerating, setRegenerating] = useState(false);

  const { control: regenerateControl, watch: watchRegenerate, setValue: setRegenerateValue } = useForm({
    defaultValues: { reason: 'NAME_CHANGE' as RegenerationReason },
  });
  const regenerateReason = watchRegenerate('reason');

  const regenerateReasonOptions = [
    { label: 'Name Change', value: 'NAME_CHANGE' },
    { label: 'Template Update', value: 'TEMPLATE_UPDATE' },
    { label: 'Data Correction', value: 'DATA_CORRECTION' },
    { label: 'Admin Requested', value: 'ADMIN_REQUESTED' },
  ];

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, DEFAULT_DEBOUNCE_DELAY);

  // Clear search when tab changes
  useEffect(() => {
    setSearchTerm('');
  }, [activeTab]);

  // Initial data fetch
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!userId) {
        setError('User ID not provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await userAchievementsService.getUserDetails({ user_id: userId });
        if (data) {
          setUser(data);
          initialLoadDone.current = true;
        } else {
          setError('User not found');
        }
      } catch (err) {
        console.error('Error fetching user details:', err);
        setError('Failed to load user details');
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [userId]);

  // Get search_type based on active tab
  const getSearchType = (): UserAchievementSearchType => {
    switch (activeTab) {
      case 'badges': return 'badge';
      case 'course': return 'course';
      case 'certification': return 'certification';
      case 'learningpath': return 'learning_path';
      default: return 'badge';
    }
  };

  // Search effect - fetch data when debounced search changes
  useEffect(() => {
    // Skip if initial load hasn't completed or no userId
    if (!initialLoadDone.current || !userId) return;

    const searchUserDetails = async () => {
      try {
        setSearching(true);
        const data = await userAchievementsService.getUserDetails({
          user_id: userId,
          search: debouncedSearchTerm || undefined,
          search_type: debouncedSearchTerm ? getSearchType() : undefined
        });
        if (data) {
          setUser(data);
        }
      } catch (err) {
        console.error('Error searching user details:', err);
      } finally {
        setSearching(false);
      }
    };

    searchUserDetails();
  }, [debouncedSearchTerm, userId, activeTab]);

  // Helper function to get initials from name
  const getInitials = (name: string): string => {
    if (!name) return '';
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  // Open regenerate modal for a certificate
  const openRegenerateModal = (certificateId: string, certType: CertificateType) => {
    setRegenerateCertId(certificateId);
    setRegenerateCertType(certType);
    setRegenerateValue('reason', 'NAME_CHANGE');
    setShowRegenerateModal(true);
  };

  // Handle admin certificate regeneration
  const handleRegenerateCertificate = async () => {
    if (!regenerateCertId || !regenerateCertType) return;

    try {
      setRegenerating(true);
      await certificateRegenerationService.adminRegenerate(
        regenerateCertId,
        regenerateCertType,
        regenerateReason
      );

      toast.success('Certificate regenerated successfully!');
      setShowRegenerateModal(false);

      // Refresh user data
      if (userId) {
        const data = await userAchievementsService.getUserDetails({ user_id: userId });
        if (data) setUser(data);
      }
    } catch (err: any) {
      const errorData = err?.response?.data;
      const errorCode = errorData?.error?.error;
      const message = errorData?.error?.error_description || errorData?.error_description || errorData?.message || err?.message || 'Failed to regenerate certificate';

      if (errorCode === 'NO_CHANGES_DETECTED') {
        toast.error(message);
        setShowRegenerateModal(false);
        return;
      }

      toast.error(message);
    } finally {
      setRegenerating(false);
    }
  };

  const ProfileImage = ({ src, alt, size = 'lg' }: { src?: string; alt: string; size?: 'sm' | 'md' | 'lg' | 'xl' }) => {
    const sizeClasses = {
      sm: 'w-10 h-10',
      md: 'w-12 h-12',
      lg: 'w-20 h-20',
      xl: 'w-24 h-24'
    };
    const textSizes = {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-xl',
      xl: 'text-2xl'
    };
    const hasImage = src && src.trim() !== '';
    const initials = getInitials(alt);

    if (hasImage) {
      return <img src={src} alt={alt} className={`${sizeClasses[size]} rounded-full object-cover`} />;
    }

    return (
      <div className={`${sizeClasses[size]} rounded-full bg-gray-200 flex items-center justify-center`}>
        <span className={`${textSizes[size]} font-semibold text-gray-600`}>{initials}</span>
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const handleViewCertificate = (certificateNumber: string) => {
    window.open(`/certificate/verify/${certificateNumber}`, '_blank');
  };

  const CourseCertificateCard = ({ cert }: { cert: CourseCertificate }) => {
    return (
      <div className="bg-white rounded-xl border-2 border-brand-blue-200 hover:border-brand-blue-300 overflow-hidden hover:shadow-lg transition-all group">
        <div className="h-32 bg-gradient-to-br from-brand-blue-500 to-brand-blue-700 flex items-center justify-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 30% 30%, white 1px, transparent 1px)',
              backgroundSize: '20px 20px'
            }}></div>
          </div>
          {cert.banner_image_url ? (
            <img src={cert.banner_image_url} alt={cert.course_title} className="w-full h-full object-cover" />
          ) : (
            <div className="relative flex flex-col items-center">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-2 border-2 border-white/30">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <Award className="w-5 h-5 text-white/80" />
            </div>
          )}
        </div>
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 text-sm line-clamp-2 mb-2 group-hover:text-gray-700 transition-colors" title={cert.course_title}>
            {cert.course_title}
          </h3>
          <div className="flex items-center gap-1 text-xs text-gray-500 mb-3">
            <Calendar className="w-3 h-3" />
            <span>{formatDate(cert.issued_at)}</span>
          </div>
          <div className="flex items-center gap-2">
            {cert.certificate_number && (
              <button
                onClick={() => handleViewCertificate(cert.certificate_number!)}
                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-white bg-brand-blue-600 hover:bg-brand-blue-700 rounded-lg transition-colors"
                title="View Certificate"
              >
                <ExternalLink className="w-3 h-3" />
                View
              </button>
            )}
            <button
              onClick={() => openRegenerateModal(cert.certificate_id, 'course')}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-brand-blue-600 hover:text-brand-blue-700 border border-brand-blue-200 hover:bg-brand-blue-50 rounded-lg transition-colors"
              title="Regenerate Certificate"
            >
              <RefreshCw className="w-3 h-3" />
              Regenerate
            </button>
          </div>
        </div>
      </div>
    );
  };

  const CertificationCertificateCard = ({ cert }: { cert: CertificationCertificate }) => {
    return (
      <div className="bg-white rounded-xl border-2 border-brand-amber-200 hover:border-brand-amber-300 overflow-hidden hover:shadow-lg transition-all group">
        <div className="h-32 bg-gradient-to-br from-brand-amber-500 to-brand-orange-600 flex items-center justify-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 30% 30%, white 1px, transparent 1px)',
              backgroundSize: '20px 20px'
            }}></div>
          </div>
          {cert.banner_image_url ? (
            <img src={cert.banner_image_url} alt={cert.certification_title} className="w-full h-full object-cover" />
          ) : (
            <div className="relative flex flex-col items-center">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-2 border-2 border-white/30">
                <GraduationCap className="w-8 h-8 text-white" />
              </div>
              <Award className="w-5 h-5 text-white/80" />
            </div>
          )}
        </div>
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 text-sm line-clamp-2 mb-2 group-hover:text-gray-700 transition-colors" title={cert.certification_title}>
            {cert.certification_title}
          </h3>
          <div className="flex items-center gap-1 text-xs text-gray-500 mb-3">
            <Calendar className="w-3 h-3" />
            <span>{formatDate(cert.issued_at)}</span>
          </div>
          <div className="flex items-center gap-2">
            {cert.certificate_number && (
              <button
                onClick={() => handleViewCertificate(cert.certificate_number!)}
                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-white bg-brand-amber-600 hover:bg-brand-amber-700 rounded-lg transition-colors"
                title="View Certificate"
              >
                <ExternalLink className="w-3 h-3" />
                View
              </button>
            )}
            <button
              onClick={() => openRegenerateModal(cert.certificate_id, 'certification')}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-brand-amber-600 hover:text-brand-amber-700 border border-brand-amber-200 hover:bg-brand-amber-50 rounded-lg transition-colors"
              title="Regenerate Certificate"
            >
              <RefreshCw className="w-3 h-3" />
              Regenerate
            </button>
          </div>
        </div>
      </div>
    );
  };

  const LearningPathCertificateCard = ({ cert }: { cert: LearningPathCertificate }) => {
    return (
      <div className="bg-white rounded-xl border-2 border-brand-green-200 hover:border-brand-green-300 overflow-hidden hover:shadow-lg transition-all group">
        <div className="h-32 bg-gradient-to-br from-brand-emerald-500 to-brand-green-600 flex items-center justify-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 30% 30%, white 1px, transparent 1px)',
              backgroundSize: '20px 20px'
            }}></div>
          </div>
          {cert.banner_image_url ? (
            <img src={cert.banner_image_url} alt={cert.learning_path_title} className="w-full h-full object-cover" />
          ) : (
            <div className="relative flex flex-col items-center">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-2 border-2 border-white/30">
                <Route className="w-8 h-8 text-white" />
              </div>
              <Award className="w-5 h-5 text-white/80" />
            </div>
          )}
        </div>
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 text-sm line-clamp-2 mb-2 group-hover:text-gray-700 transition-colors" title={cert.learning_path_title}>
            {cert.learning_path_title}
          </h3>
          <div className="flex items-center gap-1 text-xs text-gray-500 mb-3">
            <Calendar className="w-3 h-3" />
            <span>{formatDate(cert.issued_at)}</span>
          </div>
          <div className="flex items-center gap-2">
            {cert.certificate_number && (
              <button
                onClick={() => handleViewCertificate(cert.certificate_number!)}
                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-white bg-brand-green-600 hover:bg-brand-green-700 rounded-lg transition-colors"
                title="View Certificate"
              >
                <ExternalLink className="w-3 h-3" />
                View
              </button>
            )}
            <button
              onClick={() => openRegenerateModal(cert.certificate_id, 'learning_path')}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-brand-green-600 hover:text-brand-green-700 border border-brand-green-200 hover:bg-brand-green-50 rounded-lg transition-colors"
              title="Regenerate Certificate"
            >
              <RefreshCw className="w-3 h-3" />
              Regenerate
            </button>
          </div>
        </div>
      </div>
    );
  };

  const BadgeCard = ({ badge }: { badge: UserBadge }) => {
    const IconComponent = getBadgeIcon(badge.icon_type);

    // Get rarity-based styling
    const getRarityColors = (rarity: string) => {
      switch (rarity) {
        case 'legendary':
          return {
            card: 'bg-gradient-to-br from-amber-50 to-yellow-100 border-yellow-300',
            iconBg: 'bg-gradient-to-br from-yellow-400 to-amber-500',
            badge: 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white',
            glow: 'shadow-lg shadow-yellow-200/50'
          };
        case 'epic':
          return {
            card: 'bg-gradient-to-br from-purple-50 to-violet-100 border-purple-300',
            iconBg: 'bg-gradient-to-br from-purple-500 to-violet-600',
            badge: 'bg-gradient-to-r from-purple-500 to-violet-600 text-white',
            glow: 'shadow-lg shadow-purple-200/50'
          };
        case 'rare':
          return {
            card: 'bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-300',
            iconBg: 'bg-gradient-to-br from-blue-500 to-indigo-600',
            badge: 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white',
            glow: 'shadow-md shadow-blue-200/50'
          };
        default:
          return {
            card: 'bg-gradient-to-br from-slate-50 to-gray-100 border-gray-300',
            iconBg: 'bg-gradient-to-br from-slate-500 to-gray-600',
            badge: 'bg-gradient-to-r from-slate-500 to-gray-600 text-white',
            glow: ''
          };
      }
    };

    const colors = getRarityColors(badge.rarity);

    return (
      <div
        className={`relative p-4 rounded-xl border-2 transition-all hover:scale-105 cursor-pointer ${colors.card} ${colors.glow}`}
      >
        {badge.rarity === 'legendary' && (
          <div className="absolute -top-1 -right-1">
            <Sparkles className="w-5 h-5 text-yellow-500" />
          </div>
        )}

        {/* Badge Icon Container */}
        <div className={`w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center ${colors.iconBg} shadow-md`}>
          {badge.badge_url ? (
            <img src={badge.badge_url} alt={badge.badge_name} className="w-12 h-12 object-contain rounded-full" />
          ) : (
            <IconComponent className="w-8 h-8 text-white drop-shadow-sm" />
          )}
        </div>

        <h3 className="font-bold text-center text-sm mb-1 truncate text-gray-800" title={badge.badge_name}>{badge.badge_name}</h3>
        <p className="text-xs text-center text-gray-600 line-clamp-2 mb-2">{badge.description}</p>

        <div className="flex items-center justify-center gap-1 text-xs">
          <Zap className="w-3 h-3 text-amber-500" />
          <span className="font-medium text-gray-700">{badge.xp_required} XP required</span>
        </div>

        <div className="text-xs text-center text-gray-500 mt-2 font-medium">{badge.category}</div>

        {/* Rarity Badge */}
        <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${colors.badge} shadow-sm`}>
          {badge.rarity}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Back Button Skeleton */}
        <SkeletonLoader type="custom" className="h-6 w-52 rounded" />

        {/* Profile Header Skeleton */}
        <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-brand-purple-800 via-brand-purple-700 to-brand-purple-800 p-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Profile Image Skeleton */}
            <div className="flex flex-col items-center md:items-start">
              <SkeletonLoader type="custom" className="w-24 h-24 rounded-full bg-slate-600" />
            </div>

            {/* User Info Skeleton */}
            <div className="flex-1 text-center md:text-left md:pt-2 space-y-3">
              <SkeletonLoader type="custom" className="h-7 w-48 rounded bg-slate-600 mx-auto md:mx-0" />
              <SkeletonLoader type="custom" className="h-4 w-56 rounded bg-slate-600 mx-auto md:mx-0" />
              <div className="flex flex-wrap justify-center md:justify-start gap-2">
                <SkeletonLoader type="custom" className="h-7 w-20 rounded-full bg-slate-600" />
                <SkeletonLoader type="custom" className="h-7 w-28 rounded-full bg-slate-600" />
                <SkeletonLoader type="custom" className="h-7 w-24 rounded-full bg-slate-600" />
              </div>
              <SkeletonLoader type="custom" className="h-16 w-full max-w-md rounded-xl bg-slate-600 mx-auto md:mx-0" />
            </div>

            {/* Quick Stats Skeleton */}
            <div className="grid grid-cols-4 md:grid-cols-2 gap-3 md:pt-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonLoader key={i} type="custom" className="h-20 w-full rounded-xl bg-slate-600" />
              ))}
            </div>
          </div>
        </div>

        {/* Tabs Skeleton */}
        <div className="flex items-center gap-2 bg-gray-100 rounded-xl p-1.5">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonLoader key={i} type="custom" className="h-11 flex-1 rounded-lg" />
          ))}
        </div>

        {/* Content Skeleton */}
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <SkeletonLoader type="custom" className="w-10 h-10 rounded-xl" />
            <div className="space-y-2">
              <SkeletonLoader type="custom" className="h-6 w-36 rounded" />
              <SkeletonLoader type="custom" className="h-4 w-24 rounded" />
            </div>
          </div>

          {/* Search Skeleton */}
          <SkeletonLoader type="custom" className="h-12 w-full rounded-xl mb-6" />

          {/* Grid Skeleton */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonLoader key={i} type="custom" className="h-44 w-full rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate('/admin/gamification/user-achievements')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Back to Learner Achievements</span>
        </button>

        <div className="card overflow-hidden">
          {/* Header Banner */}
          <div className="bg-gradient-to-br from-brand-purple-800 via-brand-purple-700 to-brand-purple-800 px-6 py-8 relative">
            <div className="absolute inset-0 opacity-30">
              <div className="absolute inset-0" style={{
                backgroundImage: 'linear-gradient(135deg, transparent 25%, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.05) 50%, transparent 50%, transparent 75%, rgba(255,255,255,0.05) 75%)',
                backgroundSize: '20px 20px'
              }}></div>
            </div>
            <div className="relative z-10 flex flex-col items-center">
              <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mb-4 border-2 border-white/20">
                <Trophy className="w-10 h-10 text-slate-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-1">User Details Not Found</h2>
              <p className="text-slate-400 text-sm">
                {error || 'Unable to load achievement details'}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="p-8 text-center">
            <div className="max-w-md mx-auto">
              <p className="text-gray-600 mb-6">
                We couldn't find the achievement details for this user. This might be because:
              </p>
              <ul className="text-left text-gray-500 space-y-2 mb-8">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
                  <span>The user ID is invalid or doesn't exist</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
                  <span>There was a network or server error</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
                  <span>The user has no achievement data yet</span>
                </li>
              </ul>
              <button
                onClick={() => navigate('/admin/gamification/user-achievements')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Learner Achievements
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/admin/gamification/user-achievements')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors group"
      >
        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
        <span className="font-medium">Back to Learner Achievements</span>
      </button>

      {/* Profile Header - Full Background Design */}
      <div className="rounded-2xl shadow-xl overflow-hidden bg-gradient-to-br from-brand-purple-800 via-brand-purple-700 to-brand-purple-800 relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(135deg, transparent 25%, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.05) 50%, transparent 50%, transparent 75%, rgba(255,255,255,0.05) 75%)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        {/* Profile Content */}
        <div className="relative z-10 p-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Profile Image */}
            <div className="flex flex-col items-center md:items-start">
              <div className="relative">
                <div className="p-1 bg-white/20 backdrop-blur-sm rounded-full shadow-xl border-2 border-white/30">
                  <ProfileImage src={user.profile_image} alt={user.username} size="xl" />
                </div>
                {user.rank === 1 && (
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center border-2 border-white shadow-lg">
                    <Crown className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>

              {/* Top Performer Badge */}
              {user.rank === 1 && (
                <div className="mt-3 inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-yellow-400/20 to-amber-400/20 backdrop-blur-sm rounded-full border border-yellow-400/30">
                  <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                  <span className="text-xs font-semibold text-yellow-300">Top Performer</span>
                </div>
              )}
            </div>

            {/* User Info */}
            <div className="flex-1 text-center md:text-left md:pt-2">
              <h1 className="text-2xl font-bold text-white mb-1">{user.username}</h1>
              <p className="text-slate-400 text-sm mb-4">{user.email}</p>

              <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-4">
                {user.employee_id && (
                  <span className="px-3 py-1 bg-white/10 text-slate-200 rounded-full text-sm font-medium backdrop-blur-sm border border-white/10">
                    ID: {user.employee_id}
                  </span>
                )}
                <span className="px-3 py-1 bg-white/10 text-slate-300 rounded-full text-sm flex items-center gap-1 backdrop-blur-sm border border-white/10">
                  <Flame className="w-3.5 h-3.5 text-orange-400" />
                  {user.longest_streak} day streak
                </span>
                <span className="px-3 py-1 bg-white/10 text-slate-300 rounded-full text-sm flex items-center gap-1 backdrop-blur-sm border border-white/10">
                  <Trophy className="w-3.5 h-3.5 text-yellow-400" />
                  Rank {user.rank} 
                </span>
              </div>

              {/* Level Info */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 max-w-md mx-auto md:mx-0 border border-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-brand-violet-500 to-brand-purple-600 rounded-lg flex items-center justify-center">
                    <Zap className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-semibold text-white">Level {user.total_level}</span>
                  <span className="text-slate-300 ml-auto">{user.total_xp.toLocaleString()} XP</span>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-4 md:grid-cols-2 gap-3 md:pt-2">
              <div className="bg-gradient-to-br from-yellow-500/20 to-amber-500/20 backdrop-blur-sm rounded-xl p-3 text-center border border-yellow-400/20">
                <Zap className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
                <div className="text-lg font-bold text-white">{user.total_xp.toLocaleString()}</div>
                <div className="text-xs text-slate-300">XP</div>
              </div>
              <div className="bg-gradient-to-br from-orange-500/20 to-red-500/20 backdrop-blur-sm rounded-xl p-3 text-center border border-orange-400/20">
                <Flame className="w-5 h-5 text-orange-400 mx-auto mb-1" />
                <div className="text-lg font-bold text-white">{user.longest_streak}</div>
                <div className="text-xs text-slate-300">Streak</div>
              </div>
              <div className="bg-gradient-to-br from-purple-500/20 to-violet-500/20 backdrop-blur-sm rounded-xl p-3 text-center border border-purple-400/20">
                <Award className="w-5 h-5 text-purple-400 mx-auto mb-1" />
                <div className="text-lg font-bold text-white">{user.badge_count}</div>
                <div className="text-xs text-slate-300">Badges</div>
              </div>
              <div className="bg-gradient-to-br from-blue-500/20 to-indigo-500/20 backdrop-blur-sm rounded-xl p-3 text-center border border-blue-400/20">
                <GraduationCap className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                <div className="text-lg font-bold text-white">{user.total_certificates}</div>
                <div className="text-xs text-slate-300">Certs</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 bg-gray-100 rounded-xl p-1.5 overflow-x-auto">
        <button
          onClick={() => setActiveTab('badges')}
          className={`flex-1 md:flex-none px-4 md:px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap text-sm ${
            activeTab === 'badges'
              ? 'bg-white shadow-sm text-brand-purple-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Award className="w-4 h-4 inline mr-2" />
          Badges ({user.badges?.length || 0})
        </button>
        <button
          onClick={() => setActiveTab('course')}
          className={`flex-1 md:flex-none px-4 md:px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap text-sm ${
            activeTab === 'course'
              ? 'bg-white shadow-sm text-brand-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <BookOpen className="w-4 h-4 inline mr-2" />
          Course ({user.certificates?.course_certificates?.length || 0})
        </button>
        <button
          onClick={() => setActiveTab('certification')}
          className={`flex-1 md:flex-none px-4 md:px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap text-sm ${
            activeTab === 'certification'
              ? 'bg-white shadow-sm text-brand-amber-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <GraduationCap className="w-4 h-4 inline mr-2" />
          Certification ({user.certificates?.certification_certificates?.length || 0})
        </button>
        <button
          onClick={() => setActiveTab('learningpath')}
          className={`flex-1 md:flex-none px-4 md:px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap text-sm ${
            activeTab === 'learningpath'
              ? 'bg-white shadow-sm text-brand-green-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Route className="w-4 h-4 inline mr-2" />
          Learning Path ({user.certificates?.learning_path_certificates?.length || 0})
        </button>
      </div>

      {/* Badges Tab */}
      {activeTab === 'badges' && (
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-brand-purple-100 rounded-xl">
              <Award className="w-6 h-6 text-brand-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Earned Badges</h2>
              <p className="text-sm text-gray-500">
                {user.badges?.length || 0} badges {debouncedSearchTerm ? 'found' : 'collected'}
              </p>
            </div>
          </div>

          {/* Search Input - Full Width */}
          <div className="relative w-full mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search badges by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {searching ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand-purple-600" />
            </div>
          ) : user.badges && user.badges.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {user.badges.map((badge) => (
                <BadgeCard key={badge.badge_id} badge={badge} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              {debouncedSearchTerm ? (
                <>
                  <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No badges found</h3>
                  <p className="text-gray-500">Try a different search term</p>
                </>
              ) : (
                <>
                  <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Badges Yet</h3>
                  <p className="text-gray-500">Complete achievements to earn badges</p>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Course Certificates Tab */}
      {activeTab === 'course' && (
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-brand-blue-100 rounded-xl">
              <BookOpen className="w-6 h-6 text-brand-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Course Certificates</h2>
              <p className="text-sm text-gray-500">
                {user.certificates?.course_certificates?.length || 0} certificates {debouncedSearchTerm ? 'found' : 'earned'}
              </p>
            </div>
          </div>

          {/* Search Input - Full Width */}
          <div className="relative w-full mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search course certificates by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {searching ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand-blue-600" />
            </div>
          ) : user.certificates?.course_certificates && user.certificates.course_certificates.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {user.certificates.course_certificates.map((cert) => (
                <CourseCertificateCard key={cert.certificate_id} cert={cert} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              {debouncedSearchTerm ? (
                <>
                  <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No certificates found</h3>
                  <p className="text-gray-500">Try a different search term</p>
                </>
              ) : (
                <>
                  <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Course Certificates Yet</h3>
                  <p className="text-gray-500">Complete courses to earn certificates</p>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Certification Certificates Tab */}
      {activeTab === 'certification' && (
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-brand-amber-100 rounded-xl">
              <GraduationCap className="w-6 h-6 text-brand-amber-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Certification Certificates</h2>
              <p className="text-sm text-gray-500">
                {user.certificates?.certification_certificates?.length || 0} certificates {debouncedSearchTerm ? 'found' : 'earned'}
              </p>
            </div>
          </div>

          {/* Search Input - Full Width */}
          <div className="relative w-full mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search certification certificates by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-brand-amber-500 focus:border-brand-amber-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {searching ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand-amber-600" />
            </div>
          ) : user.certificates?.certification_certificates && user.certificates.certification_certificates.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {user.certificates.certification_certificates.map((cert) => (
                <CertificationCertificateCard key={cert.certificate_id} cert={cert} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              {debouncedSearchTerm ? (
                <>
                  <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No certificates found</h3>
                  <p className="text-gray-500">Try a different search term</p>
                </>
              ) : (
                <>
                  <GraduationCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Certification Certificates Yet</h3>
                  <p className="text-gray-500">Pass certification exams to earn certificates</p>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Learning Path Certificates Tab */}
      {activeTab === 'learningpath' && (
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-brand-green-100 rounded-xl">
              <Route className="w-6 h-6 text-brand-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Learning Path Certificates</h2>
              <p className="text-sm text-gray-500">
                {user.certificates?.learning_path_certificates?.length || 0} certificates {debouncedSearchTerm ? 'found' : 'earned'}
              </p>
            </div>
          </div>

          {/* Search Input - Full Width */}
          <div className="relative w-full mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search learning path certificates by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {searching ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand-green-600" />
            </div>
          ) : user.certificates?.learning_path_certificates && user.certificates.learning_path_certificates.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {user.certificates.learning_path_certificates.map((cert) => (
                <LearningPathCertificateCard key={cert.certificate_id} cert={cert} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              {debouncedSearchTerm ? (
                <>
                  <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No certificates found</h3>
                  <p className="text-gray-500">Try a different search term</p>
                </>
              ) : (
                <>
                  <Route className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Learning Path Certificates Yet</h3>
                  <p className="text-gray-500">Complete learning paths to earn certificates</p>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Regenerate Certificate Modal */}
      <Modal
        isOpen={showRegenerateModal}
        onClose={() => !regenerating && setShowRegenerateModal(false)}
        title="Regenerate Certificate"
        subtitle="Select a reason for regeneration"
        size="sm"
        closeOnOverlayClick={!regenerating}
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setShowRegenerateModal(false)}
              disabled={regenerating}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleRegenerateCertificate}
              disabled={regenerating}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-blue-600 rounded-lg hover:bg-brand-blue-700 focus:outline-none focus:ring-2 focus:ring-brand-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {regenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Regenerating...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Regenerate
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-brand-blue-100 flex items-center justify-center">
              <RefreshCw className="w-6 h-6 text-brand-blue-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-600">
                This will regenerate the certificate with updated information. Please select the reason for regeneration below.
              </p>
            </div>
          </div>
          <div>
            <Select
              name="reason"
              control={regenerateControl}
              label="Reason"
              options={regenerateReasonOptions}
              isSearchable={false}
              isDisabled={regenerating}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
